import axios from "axios";
import useAuthStore from "../store/useAuthStore";
import { toast } from "react-toastify";
import { instalarCifrado } from "../utils/cifrado";

// Use environment variable or fallback to localhost
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const instance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 second timeout
  headers: {
    "Content-Type": "application/json",
  },
});

/* CIFRADO DE EXTREMO A EXTREMO (RSA-2048 + AES-256-GCM).
 *
 * VA AQUÍ ARRIBA, ANTES QUE LOS DEMÁS INTERCEPTORES, y no es estético: axios
 * apila los de PETICIÓN al revés —el último registrado corre primero—, así
 * que registrándolo el primero se ejecuta el ÚLTIMO y cifra el cuerpo cuando
 * el token ya está puesto en las cabeceras. Los de RESPUESTA sí corren en
 * orden de registro, de modo que éste descifra ANTES de que el manejador de
 * abajo lea `error.response.data.message`.
 *
 * Moverlo debajo rompe las dos cosas a la vez, en silencio.
 */
instalarCifrado(instance);

// Request interceptor
instance.interceptors.request.use(
  (config) => {
    const { token, residentialSeleccionada } = useAuthStore.getState();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    /*
     * Residencial elegida por el superadministrador.
     *
     * El backend IGNORA esta cabecera para cualquier otro rol: la residencial
     * sale del usuario en la base. Mandarla de más nunca amplía permisos.
     */
    if (residentialSeleccionada) {
      config.headers["X-Residential-Id"] = residentialSeleccionada;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { logout } = useAuthStore.getState();

    if (error.response) {
      // Handle specific error codes
      switch (error.response.status) {
        case 440:
          // Sesión de cifrado vencida. `utils/cifrado.js` ya rehízo el
          // handshake y reintentó; si se llega aquí, falló dos veces.
          toast.error("Se perdió la sesión segura. Recargue la página.");
          break;

        case 401:
          // Token expired or invalid
          if (error.response.data?.code === "TOKEN_EXPIRED") {
            toast.error(
              "Tu sesión ha expirado. Por favor, inicia sesión nuevamente."
            );
          } else {
            toast.error("No autorizado. Por favor, inicia sesión.");
          }
          logout();
          window.location.href = "/login";
          break;

        case 403:
          toast.error("No tienes permisos para realizar esta acción.");
          break;

        case 404:
          toast.error("Recurso no encontrado.");
          break;

        case 422:
        case 400:
          // Validation errors
          if (error.response.data?.errors) {
            const firstError = error.response.data.errors[0];
            toast.error(
              firstError.msg || firstError.message || "Error de validación"
            );
          } else {
            toast.error(
              error.response.data?.message || "Error en la solicitud"
            );
          }
          break;

        case 500:
          toast.error("Error del servidor. Por favor, intenta más tarde.");
          break;

        default:
          toast.error(error.response.data?.message || "Ha ocurrido un error");
      }
    } else if (error.request) {
      // Request made but no response
      toast.error("No se pudo conectar con el servidor. Verifica tu conexión.");
    } else {
      // Something else happened
      toast.error("Ha ocurrido un error inesperado");
    }

    return Promise.reject(error);
  }
);

export default instance;
